<template>
    <footer class="bg-primary-bg no-print" aria-labelledby="footer-heading"
            id="footer-section">

        <h2 id="footer-heading" class="sr-only">Footer</h2>
        <div
            class="max-w-4xl laptop-sm:max-w-screen-lg desktop:max-w-screen-xl ultra-wide:max-w-screen-2xl pt-8 laptop-sm:pt-16 mx-auto pb-2 px-8 pt-16 mx-auto px-6"
        >
            <!--            add thin border -->
            <div
                class="gap-4 desktop:gap-8 grid-cols-1 laptop-lg:grid-cols-6 items-center laptop-lg:items-start grid flex justify-center"
            >
                <img
                    id="footer-remax-logo"
                    class="col-span-6 laptop-lg:col-span-2 h-auto tablet-sm:h-7 align-center"
                    src="/images/theme/footer-logo.webp"
                    alt="Remax Specialists"
                    loading="lazy"
                />
                <div
                    class="w-full ml-0 tablet-sm:ml-auto col-span-6 laptop-lg:col-span-4 mt-4 laptop-sm:mt-8
                    laptop-lg:mt-0 flex flex-wrap justify-between gap-x-8"
                >
                    <!-- Flexbox for child elements -->
                    <div class="flex flex-wrap tablet-sm:flex-nowrap gap-2 w-full">
                        <div class="mb-3 tablet-sm:mb-auto w-full tablet-sm:w-1/4">
                            <Link :href="'/'" preserve-state>
                                <FooterHeader>Home</FooterHeader>
                            </Link>
                            <ul role="list" class="relative pl-2 border-l-2 border-accent mt-4">
                                <li v-for="item in navigation.general" :key="item.name" class="relative w-full group">
                                    <Link
                                        :href="item.href"
                                        class="border-accent border-b tablet-sm:border-none text-xl tablet-sm:text-lg relative leading-6 text-primary-text group-hover:text-link-text w-full block py-2"
                                    >
                                        {{ item.name }}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div class="mb-3 tablet-sm:mb-auto w-full tablet-sm:w-1/4">
                            <FooterHeader>Inquiries</FooterHeader>
                            <ul role="list" class="relative pl-2 border-l-2 border-accent mt-4">
                                <li v-for="item in navigation.inquiries" :key="item.name" class="relative w-full group">
                                    <Link
                                        :href="item.href"
                                        class="border-accent border-b tablet-sm:border-none text-xl tablet-sm:text-lg relative text-lg leading-6 text-primary-text group-hover:text-link-text w-full block py-2"
                                    >
                                        {{ item.name }}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div class="mb-3 tablet-sm:mb-auto w-full tablet-sm:w-1/4">
                            <FooterHeader>Resources</FooterHeader>
                            <ul role="list" class="relative pl-2 border-l-2 border-accent mt-4">
                                <li v-for="item in navigation.resources" :key="item.name" class="relative w-full group">
                                    <Link
                                        :href="item.href"
                                        class="border-accent border-b tablet-sm:border-none text-xl tablet-sm:text-lg relative text-lg leading-6 text-primary-text group-hover:text-link-text w-full block py-2"
                                    >
                                        {{ item.name }}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div class="mb-3 tablet-sm:mb-auto w-full tablet-sm:w-1/4 mt-0">
                            <FooterHeader>Tools</FooterHeader>
                            <ul role="list" class="relative pl-2 border-l-2 border-accent mt-4">
                                <li v-for="item in navigation.tools" :key="item.name" class="relative w-full group">
                                    <Link
                                        :href="item.href"
                                        class="border-accent border-b tablet-sm:border-none text-xl tablet-sm:text-lg relative text-lg leading-6 text-primary-text group-hover:text-link-text w-full block py-2"
                                    >
                                        {{ item.name }}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-8"></div>
                </div>
            </div>
            <div
                class="justify-center tablet-sm:justify-auto flex flex-wrap gap-y-4 tablet-sm:gap-y-2 border-t border-accent pt-4 mt-8 text-center"
            >
                <p class="w-full text-xs leading-3 text-primary-text order-1 mt-0">
                    All information deemed reliable but not guaranteed. Each RE/MAX office is independently owned &
                    operated.
                </p>
                <p>This site is protected by reCAPTCHA and the Google <a
                    href="https://policies.google.com/privacy">Privacy Policy</a> and <a
                    href="https://policies.google.com/terms">Terms of Service</a></p>
                <div class="w-full flex justify-center items-center gap-x-2 relative">
                    <p class="text-xs leading-3 text-primary-text order-1 mt-0 flex items-center">
                        <span class="inline-block left-0 mr-2">
                            <img
                                height="24px"
                                width="25px"
                                src="/images/theme/EHO_BLK_scaled.webp"
                                alt="Equal Housing Opportunity"
                            />
                        </span>
                        EHO Disclaimer: We Do Business in Accordance With the Federal Fair Housing Law
                    </p>
                </div>
                <p class="text-xs leading-3 text-primary-text order-1 mt-0">
                    /FHA/USDA/VA. The inclusion of various education, information, web links, or materials are not an
                    endorsement of the Sender or any of its employees or business partners.
                </p>
                <p class="text-xs leading-3 text-primary-text order-1 mt-0">
                    <a rel="nofollow noopener noreferrer" href="https://www.hud.gov/guidance"
                       title="Visit HUD.gov for official homebuyer guidance and FHA information"
                       target="_blank"
                    >For information directly from HUD/FHA, visit
                    https://www.hud.gov/guidance</a>
                    , <a rel="nofollow noopener noreferrer"
                         href="https://www.benefits.va.gov/HOMELOANS"
                         title="Visit VA.gov for official VA home loan information"
                         target="_blank">
                    For information directly from the VA, visit https://www.benefits.va.gov/HOMELOANS</a>
                    , <a rel="nofollow noopener noreferrer"
                         href="https://www.usda.gov/wps/portal/usda/usdahome?navid=GRANTS_LOANS"
                         title="Visit USDA.gov for grants and home loan assistance"
                         target="_blank">
                        For information directly from the USDA, visit
                        https://www.usda.gov/wps/portal/usda/usdahome?navid=GRANTS_LOANS</a>
                </p>
                <p class="text-xs leading-3 text-primary-text order-1 mt-0">
                    &copy; 2025 Gina Betbeze. All rights reserved.
                </p>
            </div>
            <!--            <div class="mt-8 border-t border-accent pt-8 sm:mt-8 lg:mt-8 lg:flex lg:items-center lg:justify-between">-->
            <!--                <div>-->
            <!--                    <h3 class="text-sm font-semibold leading-6 text-white">Subscribe to our newsletter</h3>-->
            <!--                    <p class="mt-2 text-sm leading-6 text-white">The latest news, articles, and resources, sent to your inbox weekly.</p>-->
            <!--                </div>-->
            <!--                <form class="mt-6 sm:flex sm:max-w-md lg:mt-0">-->
            <!--                    <label for="email-address" class="sr-only">Email address</label>-->
            <!--                    <input type="email" name="email-address" id="email-address" autocomplete="email" required="" class="w-full min-w-0 appearance-none rounded-md border-0 bg-white px-3 py-1.5 text-base text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:w-56 sm:text-sm sm:leading-6" placeholder="Enter your email" />-->
            <!--                    <div class="mt-4 sm:ml-4 sm:mt-0 sm:flex-shrink-0">-->
            <!--                        <button type="submit" class="flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Subscribe</button>-->
            <!--                    </div>-->
            <!--                </form>-->
            <!--            </div>-->
        </div>
    </footer>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { Link, usePage } from "@inertiajs/vue3";

import CommunityDirectory from "@/Components/PageContent/CommunityDirectory.vue"
import FooterHeader from "@/Components/PageContent/FooterHeader.vue";
const page = usePage();
const props = defineProps({
    communities: {
        // This is just the qs data
        type: Object,
        required: false,
    },
});
const navigation = {
    general: [
        { name: "About", href: "/about" },
        { name: "Current Listings", href: "/current-listings" },
    ],
    inquiries: [
        { name: "Buying", href: "/buying" },
        { name: "Selling", href: "/selling" },
        { name: "General", href: "/contact" },
    ],
    resources: [
        { name: "Neighborhoods", href: "/neighborhoods" },
        { name: "School Information", href: "/school-info" },
        { name: "County Appraiser's", href: "/county-appraisals" },
        { name: "Crime Maps", href: "/crime-maps" },
    ],
    tools: [
        { name: "Mortgage Calculator", href: "/mortgage-calculator" },
        { name: "Property Search", href: "/#property-search" },
    ],
};

</script>
